import { Kaggle } from "@kaggle/sdk";
import {
  getCurrentAccelerator,
  getCurrentDataSourceReferences,
  getCurrentDockerImageTag,
  getCurrentInternetEnabled,
  getCurrentKernelId,
  getCurrentLanguageId,
  getCurrentTitle,
  getCurrentWorkerPoolName,
} from "../../kernels/redux/selectors/current";
import { AppState } from "../../kernels/redux/types";

import KernelVersionType = Kaggle.Sdk.Kernels.KernelVersionType;

/**
 * ONLY MODIFY THIS FILE IF YOU'RE CERTAIN OF WHAT YOU'RE DOING.
 *
 * The majority of updates to a kernel
 * are done by executing the same API request with variations
 * in the parameters sent.
 *
 * For example, if you want to start a worker for an existing kernel,
 * you call `createKernel` and provide the kernel id, title and language.
 * If you'd like to add a dataset to a kernel, you call the same
 * API endpoint but this time provide all the previous parameters
 * as well as the dataset to be added. You'll find this pattern for
 * most kernel operations.
 *
 * Ideally, we'd have a specific endpoint for each operation with
 * a well defined interface so we know exactly what parameters are
 * required. We're moving in that direction but in the meantime,
 * we'll mitigate the situation with some hackery present in this module.
 *
 * The main objective of the code in this module, is to minimize
 * the areas of the frontend affected by our current API interface state.
 */

/** Default CreateKernelSessionRequest parameters. */
const DEFAULT_SESSION_PARAMS = {
  forkedFromKernelId: undefined,
  kernelVersionId: undefined,
  dockerImageVersionId: undefined,
  pinnedDockerImageVersionId: undefined,
  ipynbContents: undefined,
  diff: undefined,
  forkParentDiff: undefined,
  versionType: KernelVersionType.INTERACTIVE,
  immediatelyCancelSession: false,
};

/**
 * Maps Redux state to CreateKernelSessionRequest (command handler params).
 * @param state The Redux app state.
 */
export function mapStateToRunRequest(
  state: AppState
): Kaggle.Sdk.Kernels.CreateKernelSessionRequest {
  const kernelId = getCurrentKernelId(state);
  const languageId = getCurrentLanguageId(state);
  const title = getCurrentTitle(state);
  if (!kernelId) {
    throw new Error("KernelId is a required CreateKernelSessionRequest field.");
  }
  if (!title) {
    throw new Error("Title is a required CreateKernelSessionRequest field.");
  }
  if (!languageId) {
    throw new Error(
      "LanguageId is a required CreateKernelSessionRequest field."
    );
  }
  return {
    ...DEFAULT_SESSION_PARAMS,
    kernelId,
    parameters: {
      title,
      languageId,
      dataSources: getCurrentDataSourceReferences(state),
      compute: {
        accelerator: getCurrentAccelerator(state),
        internet: { isEnabled: getCurrentInternetEnabled(state) },
        constraints: undefined,
      },
    },
    dockerImageTag: getCurrentDockerImageTag(state),
    workerPoolName: getCurrentWorkerPoolName(state),
    useGivenDataSourceVersionIds: false,
    submissionRequestedDetails: undefined,
    executedByScheduleId: undefined,
    githubSaveRequest: undefined,
    environmentVariables: [],
  };
}
